// Reader view.
//
// Content arrives already sanitized (the server cleans on ingest, so every
// client is safe), which is why innerHTML is acceptable here — the alternative,
// sanitizing per client, is what let Yera ship stored XSS.
import { api, patchBookmarkInList, TOKEN_KEY } from './store.ts';
import { el, hostname, formatDate, readingTime, listeningTime, debounce } from '../../ui/shared/dom.ts';
import { loadPrefs, savePrefs, applyPrefs, LIMITS } from '../../ui/shared/reader-prefs.ts';
import {
  loadAnnotations, unloadAnnotations, highlightSelection, setCountListener, setSurface, paintPdfPage,
} from './annotations.ts';
import { flatten, wrapRanges } from '../../ui/shared/highlight.ts';
import { locate, normalizeText } from '../../ui/shared/anchor.ts';
import {
  openPdf, preloadPdfViewer, unloadPdf, scrollToPdfPage, onPdfPageChange, stepPdfZoom, resetPdfZoom, getPdfZoom,
  getPdfLayout, setPdfLayout, getPdfColors, setPdfColors, getPdfReading, setPdfReading,
  isPdfPaged, pdfPagedRatio, showPdfRatio, turnPdfPage,
  type PdfOutlineItem, type PdfLayout, type PdfColors, type PdfReading,
} from './pdf-viewer.ts';
import {
  enableTextPaging, disableTextPaging, isTextPaged, relayoutText, turnTextPage, goToTextPage, revealText,
  textPageReached, textPagedRatio, showTextRatio, onTextPageChange, getTextReading, setTextReading,
  type TextReading,
} from './text-pager.ts';
import { openVideo } from './video.ts';
import { openAudio } from './audio.ts';
import type { MediaPlayer } from './media.ts';
import type { NoteEditor } from '@research/editor';
import { isUploadUrl } from '../../ui/shared/upload.ts';
import { youtubeVideoId } from '../../ui/shared/youtube.ts';
import { formatTime } from '../../ui/shared/transcript.ts';
import type {
  AudiobookLinks, Bookmark, FeedEpisodeSummary, PodcastEpisodeMedia, PodcastFeedMedia, ReadingStatus, TocEntry,
} from '../../ui/shared/types.ts';

/**
 * Look up an element that the page is expected to contain.
 *
 * Throws rather than returning null: every id here is in index.html, so a
 * miss means the markup and the script have diverged — which is exactly the
 * failure that should be loud rather than a silent no-op further down.
 */
function $<T extends HTMLElement = HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) throw new Error(`Missing element #${id}`);
  return found as T;
}

/** The bookmark being read, or null when the reader is closed. */
let current: Bookmark | null = null;
let prefs = loadPrefs();
let scrollHandler: (() => void) | null = null;
let keyHandler: ((event: KeyboardEvent) => void) | null = null;
/** What the reader is showing: an article's HTML, or a PDF's pages. */
let mode: 'html' | 'pdf' = 'html';

// ── Progress ──────────────────────────────────────────────────────────────────

/**
 * How far through the article we are, 0–1.
 *
 * Measured against scrollable distance rather than document height: the last
 * viewport-worth of an article can't be scrolled past, so dividing by total
 * height would cap a finished article somewhere around 0.9 and never mark it
 * read.
 */
function currentRatio(): number {
  // Turning pages, the position is which spread is open, not the scroll.
  if (isPdfPaged()) return pdfPagedRatio();
  if (isTextPaged()) return textPagedRatio();
  if (watching) return videoRatio();
  const scrollable = document.documentElement.scrollHeight - window.innerHeight;
  if (scrollable <= 0) return 1; // article shorter than the window: it's all visible
  return Math.min(1, Math.max(0, window.scrollY / scrollable));
}

function paintProgress(ratio: number): void {
  $('reader-progress-bar').style.width = `${(ratio * 100).toFixed(1)}%`;
}

// Writes are debounced because scroll fires continuously; localStorage is
// updated immediately as an offline cache so a lost connection doesn't lose
// your place.
const persistProgress = debounce(async (id: number, ratio: number) => {
  try {
    const saved = await api.setReadingState(id, { scroll_ratio: ratio });
    patchBookmarkInList(id, {
      reading_status: saved.status,
      reading_percent: saved.percent_read,
    });
    renderStatusButton(saved.status);
  } catch {
    // Offline or logged out — the local cache below still holds the position.
  }
}, 800);

function cacheKey(id: number): string {
  return `nexus_progress_${id}`;
}

function cacheProgress(id: number, ratio: number): void {
  try {
    localStorage.setItem(cacheKey(id), String(ratio));
  } catch {
    // Private browsing may refuse writes; server-side progress still applies.
  }
}

function cachedProgress(id: number): number {
  const value = Number(localStorage.getItem(cacheKey(id)));
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function restoreScroll(ratio: number): void {
  if (!ratio) return;
  if (isPdfPaged()) {
    showPdfRatio(ratio);
    paintProgress(ratio);
    return;
  }
  if (isTextPaged()) {
    showTextRatio(ratio);
    paintProgress(ratio);
    return;
  }
  // Two frames: one for layout after innerHTML, one for the fonts/images that
  // change height. Images are lazy, so this is a best effort by design —
  // landing slightly early beats landing past what you'd read.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable > 0) window.scrollTo(0, scrollable * ratio);
      paintProgress(ratio);
    });
  });
}

// ── Status ────────────────────────────────────────────────────────────────────

const NEXT_STATUS: Record<ReadingStatus, ReadingStatus> = { unread: 'done', reading: 'done', done: 'unread' };
const STATUS_LABEL: Record<ReadingStatus, string> = {
  unread: 'mark as read', reading: 'mark as read', done: '✓ read',
};

function renderStatusButton(status: ReadingStatus): void {
  const button = $('reader-status');
  button.textContent = STATUS_LABEL[status] || STATUS_LABEL.unread;
  button.dataset.status = status;
}

async function toggleStatus(): Promise<void> {
  if (!current) return;
  const currentStatus = ($('reader-status').dataset.status || 'unread') as ReadingStatus;
  const status = NEXT_STATUS[currentStatus];
  renderStatusButton(status);
  try {
    const saved = await api.setReadingState(current.id, { status });
    patchBookmarkInList(current.id, {
      reading_status: saved.status,
      reading_percent: saved.percent_read,
    });
  } catch (err) {
    alert((err as Error).message);
  }
}

// ── Preferences ───────────────────────────────────────────────────────────────

function wirePrefs(): void {
  const controls: Array<[string, 'fontSize' | 'lineHeight' | 'measure', typeof LIMITS.fontSize, (v: unknown) => string]> = [
    ['pref-size', 'fontSize', LIMITS.fontSize, (v) => `${v}px`],
    ['pref-leading', 'lineHeight', LIMITS.lineHeight, (v) => Number(v).toFixed(1)],
    ['pref-measure', 'measure', LIMITS.measure, (v) => `${v}ch`],
  ];

  for (const [id, key, limits, format] of controls) {
    const input = $<HTMLInputElement>(id);
    const output = $(`${id}-out`);
    Object.assign(input, { min: limits.min, max: limits.max, step: limits.step });
    input.value = String(prefs[key]);
    output.textContent = format(prefs[key]);

    input.addEventListener('input', () => {
      prefs = { ...prefs, [key]: Number(input.value) };
      output.textContent = format(input.value);
      applyPrefs(prefs);
      savePrefs(prefs);
      relayoutText();
    });
  }

  const selects: Array<[string, 'font' | 'theme']> = [['pref-font', 'font'], ['pref-theme', 'theme']];
  for (const [id, key] of selects) {
    const select = $<HTMLSelectElement>(id);
    select.value = prefs[key];
    select.addEventListener('change', () => {
      prefs = { ...prefs, [key]: select.value } as typeof prefs;
      applyPrefs(prefs);
      savePrefs(prefs);
      relayoutText();
    });
  }

  const layout = $<HTMLSelectElement>('pref-pdf-layout');
  layout.value = getPdfLayout();
  layout.addEventListener('change', () => setPdfLayout(layout.value as PdfLayout));
  const colors = $<HTMLSelectElement>('pref-pdf-colors');
  colors.value = getPdfColors();
  colors.addEventListener('change', () => setPdfColors(colors.value as PdfColors));
  // One Reading control, kept separately for PDFs and for text: a PDF can be
  // worth turning page by page while articles still scroll.
  const reading = $<HTMLSelectElement>('pref-reading');
  reading.addEventListener('change', () => {
    if (mode === 'pdf') {
      setPdfReading(reading.value as PdfReading);
    } else {
      setTextReading(reading.value as TextReading);
      if (current?.article?.has_content && !watching) {
        if (reading.value === 'paged') enableTextPaging($('reader-article'), $('reader-content'));
        else disableTextPaging();
        watchEpubImages();
      }
    }
    syncPageButtons();
    scrollHandler?.();
  });

  $('reader-prefs-btn').addEventListener('click', () => {
    const panel = $('reader-prefs');
    const open = panel.classList.toggle('hidden');
    $('reader-prefs-btn').setAttribute('aria-expanded', String(!open));
  });
}

// ── Rendering ─────────────────────────────────────────────────────────────────

let coverUrl: string | null = null;

function releaseCover(): void {
  if (coverUrl) URL.revokeObjectURL(coverUrl);
  coverUrl = null;
  $('reader-header').classList.remove('has-cover');
}

/** An EPUB's cover, beside its title. Fetched (with the login token, like its
 *  other images) only for a book that has one; a 404 just means it doesn't. */
async function loadEpubCover(id: number): Promise<void> {
  let url: string;
  try {
    const response = await fetch(`${api.baseUrl}/bookmarks/${id}/epub/cover`, {
      headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
    });
    if (!response.ok) return;
    url = URL.createObjectURL(await response.blob());
  } catch {
    return;
  }
  // Gone somewhere else while the cover was on its way.
  if (current?.id !== id) {
    URL.revokeObjectURL(url);
    return;
  }
  coverUrl = url;
  $('reader-header').classList.add('has-cover');
  $('reader-header').prepend(el('img', { class: 'reader-cover', src: url, alt: `Cover of ${current.title ?? 'this book'}` }));
}

function renderHeader(bookmark: Bookmark): void {
  releaseCover();
  const article: Partial<NonNullable<Bookmark['article']>> = bookmark.article ?? {};
  const media = article.media;
  // A podcast or an audiobook says how long it is to listen to, a show how
  // many episodes it has.
  const length = media?.kind === 'episode'
    ? (media.duration ? `${Math.round(media.duration / 60)} min` : null)
    : media?.kind === 'audiobook'
      ? listeningTime(media.duration)
    : media?.kind === 'feed'
      ? `${media.episode_count} episode${media.episode_count === 1 ? '' : 's'}`
      : readingTime(article.word_count);
  const bits = [
    article.site_name || hostname(bookmark.url),
    article.byline,
    formatDate(article.published_at || bookmark.created_at),
    length,
  ].filter(Boolean);

  // A show's artwork beside the title, as a book's cover is.
  const artwork = media?.image;
  if (artwork) $('reader-header').classList.add('has-cover');

  $('reader-header').replaceChildren(
    ...(artwork ? [el('img', { class: 'reader-cover', src: artwork, alt: '', loading: 'lazy', referrerpolicy: 'no-referrer' })] : []),
    el('h1', {}, bookmark.title || bookmark.url),
    el('div', { class: 'reader-meta' }, bits.join(' · ')),
    // An uploaded file has no original page to go back to.
    ...(isUploadUrl(bookmark.url) ? [] : [el('a', {
      class: 'reader-source',
      href: bookmark.url,
      target: '_blank',
      rel: 'noopener noreferrer',
    }, 'view original ↗')]),
  );
}

// The notes field: a plain textarea at once, upgraded to the rich-text editor
// (@research/editor) when that has loaded. Either way the note is Markdown text,
// so if the editor fails to load the textarea simply stays and still works.
let notes: {
  editor: NoteEditor | null;
  /** Save now if an edit is still waiting out the debounce. */
  flush: () => void;
} | null = null;

function closeNotes(): void {
  if (!notes) return;
  notes.flush();
  void notes.editor?.destroy();
  notes = null;
}

function renderFooter(bookmark: Bookmark): void {
  closeNotes();
  $('reader-footer').replaceChildren(
    el('div', { class: 'reader-note-label' }, 'Notes'),
    el('textarea', {
      id: 'reader-note',
      rows: 4,
      placeholder: 'Your notes on this article...',
    }, bookmark.note || ''),
    el('div', { class: 'reader-note-status', id: 'reader-note-status' }, '')
  );

  const note = $<HTMLTextAreaElement>('reader-note');
  const status = $('reader-note-status');
  const state: NonNullable<typeof notes> = { editor: null, flush: () => {} };
  notes = state;

  let dirty = false;
  // Reads the note when the debounce fires, not on each keystroke: from the
  // editor that is a Markdown conversion of the whole document.
  const save = debounce(async () => {
    if (!dirty) return;
    dirty = false;
    const value = state.editor ? state.editor.getData() : note.value;
    try {
      await api.updateBookmark(bookmark.id, { note: value });
      patchBookmarkInList(bookmark.id, { note: value });
      status.textContent = 'saved';
      setTimeout(() => { status.textContent = ''; }, 1500);
    } catch (err) {
      status.textContent = (err as Error).message;
    }
  }, 900);
  state.flush = () => save.flush();

  const edited = () => {
    dirty = true;
    status.textContent = 'saving...';
    save();
  };
  note.addEventListener('input', edited);

  import('@research/editor')
    .then(({ createNoteEditor }) => createNoteEditor(note, edited))
    .then((editor) => {
      // The reader moved on (another article, or closed) while it loaded.
      if (notes !== state) return void editor.destroy();
      state.editor = editor;
    })
    .catch((err) => {
      console.warn('Rich-text notes unavailable, using plain text:', err);
    });
}

/**
 * Toggle between the HTML article view and the PDF view. Notes and the
 * highlight panel work in both — a PDF page's text layer is highlighted just
 * like an article. The typography prefs mean nothing for a canvas-rendered
 * page, so for a PDF the Aa panel swaps them for page layout and colours.
 */
function setReaderMode(next: 'html' | 'pdf'): void {
  mode = next;
  const isPdf = mode === 'pdf';
  $('reader-tts').hidden = false;
  disableTextPaging({ keepPlace: false });
  closeMediaReader();
  setSurface(mode);
  $('reader-article').classList.toggle('is-pdf', isPdf);
  $('reader-content').hidden = isPdf;
  $('pdf-content').hidden = !isPdf;
  $('reader-prefs').classList.toggle('is-pdf', isPdf);
  $('reader-download-pdf').hidden = !isPdf;
  $('pdf-zoom').hidden = !isPdf;
  $<HTMLSelectElement>('pref-reading').value = isPdf ? getPdfReading() : getTextReading();
  syncPageButtons();
  // The EPUB/PDF navigation dropdown resets here too, regardless of mode —
  // showToc() is what fills it again, only once the content has something to list.
  $('reader-download-epub').hidden = true;
  $('reader-toc-btn').hidden = true;
  $('reader-toc').classList.add('hidden');
  $('reader-toc').replaceChildren();
  navTargets = [];
}

interface NavEntry {
  label: string;
  depth?: number;
  go: () => void;
  /** Where the entry points, if that's an element on the page — lets the
   *  dropdown show which entry you are currently reading. */
  target?: () => HTMLElement | null;
}

/** The dropdown's buttons alongside where each points, in list order. */
let navTargets: Array<{ button: HTMLElement; target?: () => HTMLElement | null }> = [];

/** Fill the navigation dropdown; the button stays hidden when there's nothing to list. */
function showToc(buttonLabel: string, entries: NavEntry[]): void {
  const button = $('reader-toc-btn');
  button.hidden = entries.length === 0;
  button.textContent = buttonLabel;
  button.title = buttonLabel;

  const buttons = entries.map(({ label, depth = 0, go }) => {
    const item = el('button', {
      class: 'btn-sm',
      type: 'button',
      onclick: () => {
        go();
        $('reader-toc').classList.add('hidden');
        button.setAttribute('aria-expanded', 'false');
      },
    }, label);
    item.style.setProperty('--depth', String(depth));
    return item;
  });
  navTargets = entries.map((entry, i) => ({ button: buttons[i]!, target: entry.target }));
  $('reader-toc').replaceChildren(...buttons);
}

/**
 * Build the chapter-jump dropdown for an EPUB by scanning the rendered
 * article for the `data-epub-chapter` markers `processEpub()`
 * (api/lib/epub.ts) wraps each chapter in — a lightweight stand-in for real
 * book navigation, not a schema-backed table of contents.
 */
function updateEpubChapters(toc: TocEntry[] | null | undefined): void {
  $('reader-download-epub').hidden = false;

  // The book's own table of contents, when it declared one.
  if (toc?.length) {
    showToc('Contents', toc.map(({ title, href, depth }) => ({
      label: title,
      depth,
      go: () => reveal(findFragmentTarget(href)),
      target: () => findFragmentTarget(href),
    })));
    return;
  }

  // An EPUB stored before contents were kept: a list guessed from the headings.
  const chapterEls = [...$('reader-content').querySelectorAll<HTMLElement>('[data-epub-chapter]')];
  showToc('Chapters', chapterEls.map((chapterEl, i) => ({
    label: chapterEl.querySelector('h1, h2, h3')?.textContent?.trim() || `Chapter ${i + 1}`,
    go: () => reveal(chapterEl),
    target: () => chapterEl,
  })));
}

/** Bring a place in the article into view: its page when turning pages. */
function reveal(target: Element | null): void {
  if (!target) return;
  if (isTextPaged()) revealText(target);
  else target.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/** Mark the entry whose target you have most recently scrolled past, and bring it
 *  into view in the list. Done when the list is opened rather than on every
 *  scroll: it's only ever looked at then. */
function markCurrentNavEntry(): void {
  const line = readingLine();
  let current: HTMLElement | null = null;
  for (const { button, target } of navTargets) {
    button.removeAttribute('aria-current');
    const element = target?.();
    if (!element) continue;
    if (isTextPaged() ? textPageReached(element) : element.getBoundingClientRect().top <= line) current = button;
  }
  current?.setAttribute('aria-current', 'true');
  current?.scrollIntoView({ block: 'nearest' });
}

/** Wire up the page box, zoom buttons and outline for a PDF that has just opened. */
function setupPdfControls(pageCount: number, loadOutline: () => Promise<PdfOutlineItem[]>): void {
  const input = $<HTMLInputElement>('page-input');
  input.max = String(pageCount);
  input.value = '1';
  $('page-total').textContent = `/ ${pageCount}`;
  updateZoomLabel();

  // Not while the box has focus: someone typing a page number shouldn't have it
  // rewritten under them by the scroll position.
  onPdfPageChange((page) => {
    if (document.activeElement !== input) input.value = String(page);
    // A turn doesn't scroll, so save the position here instead.
    if (isPdfPaged()) scrollHandler?.();
  });
  syncPageButtons();

  // Best effort, and after the document is already readable.
  loadOutline().then((items) => {
    showToc('Outline', items.map(({ title, page, depth }) => ({
      label: title, depth, go: () => scrollToPdfPage(page),
    })));
  }).catch((err) => console.error('Failed to read the PDF outline', err));
}

/** A PDF always has its page box; text only while its pages are turned. The
 *  ‹ › buttons are only for turning pages. */
function syncPageButtons(): void {
  const paged = mode === 'pdf' ? isPdfPaged() : isTextPaged();
  $('page-controls').hidden = mode !== 'pdf' && !paged;
  $('page-prev').hidden = !paged;
  $('page-next').hidden = !paged;
}

// Text pages are counted afresh by every layout, so the box follows both.
onTextPageChange((page, count) => {
  const input = $<HTMLInputElement>('page-input');
  input.max = String(count);
  if (document.activeElement !== input) input.value = String(page);
  $('page-total').textContent = `/ ${count}`;
  // A turn doesn't scroll, so save the position here instead.
  scrollHandler?.();
});

function updateZoomLabel(): void {
  $('pdf-zoom-level').textContent = `${Math.round(getPdfZoom() * 100)}%`;
}

/**
 * The article's own title, repeated as its first heading, is what a markdown
 * file (or a web article) usually opens with — and the header above has just
 * shown it. Hidden rather than removed, so the text is still there for
 * highlights and read-aloud to anchor to.
 */
function hideDuplicateTitle(title: string | null): void {
  const first = $('reader-content').firstElementChild;
  const same = (a: string | null | undefined, b: string | null | undefined): boolean =>
    Boolean(a && b) && a!.replace(/\s+/g, ' ').trim().toLowerCase() === b!.replace(/\s+/g, ' ').trim().toLowerCase();
  if (first?.tagName === 'H1' && same(first.textContent, title)) (first as HTMLElement).hidden = true;
}

/** A Contents dropdown from a markdown document's headings (only its own carry
 *  the `md-` ids), when there are enough of them to be worth navigating. */
function updateHeadingContents(): void {
  const headings = [...$('reader-content').querySelectorAll<HTMLElement>('h1[id], h2[id], h3[id]')]
    .filter((heading) => heading.id.startsWith('md-') && !heading.hidden);
  if (headings.length < 3) return;

  const top = Math.min(...headings.map((heading) => Number(heading.tagName[1])));
  showToc('Contents', headings.map((heading) => ({
    label: heading.textContent?.trim() || heading.id,
    depth: Number(heading.tagName[1]) - top,
    go: () => reveal(heading),
    target: () => heading,
  })));
}

// ── An EPUB's stored images ───────────────────────────────────────────────────
// A book's larger images are files, not part of the article (see lib/epub.ts):
// its <img>s carry a placeholder and an id, `epub-img-<file>-<n>`. An <img>
// can't send the login token, so each is fetched here — only once it is near
// the viewport — and the browser is handed a blob URL.

const IMAGE_ID = /^epub-img-(\d+)-/;
const IMAGE_MARGIN = '1500px 0px';
const IMAGE_CONCURRENCY = 4;

let imageObserver: IntersectionObserver | null = null;
/** File number → its blob URL, in flight or done: a picture used twice is fetched once. */
const imageLoads = new Map<string, Promise<string | null>>();
let imagesFetching = 0;
const imageQueue: Array<() => void> = [];

async function fetchEpubImage(bookmarkId: number, file: string): Promise<string | null> {
  // A few at a time: a page of plates shouldn't open forty connections at once.
  if (imagesFetching >= IMAGE_CONCURRENCY) await new Promise<void>((resolve) => imageQueue.push(resolve));
  imagesFetching++;
  try {
    const response = await fetch(`${api.baseUrl}/bookmarks/${bookmarkId}/epub/image/${file}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
    });
    return response.ok ? URL.createObjectURL(await response.blob()) : null;
  } catch {
    return null; // offline, say; the placeholder stays and the text is unaffected
  } finally {
    imagesFetching--;
    imageQueue.shift()?.();
  }
}

/** Size a book's stored images up front, before any of them arrive. */
function reserveEpubImages(): void {
  const images = $('reader-content').querySelectorAll<HTMLImageElement>('img[id^="epub-img-"]');

  // Reserve each picture's space. The placeholder is itself a (1x1) image, and
  // once loaded its own 1:1 shape beats the ratio the width/height attributes
  // imply — so a 578x1200 plate would reserve 578x578 and push everything below
  // it down by 600px when it arrived. Stated explicitly, the ratio holds.
  for (const img of images) {
    const width = Number(img.getAttribute('width'));
    const height = Number(img.getAttribute('height'));
    if (width > 0 && height > 0) img.style.aspectRatio = `${width} / ${height}`;
  }
}

/**
 * Watch a book's stored images; each loads as it nears the viewport. Turning
 * pages, "near" is the page either side: those are clipped out of the
 * viewport altogether, so the article is the root instead, widened by a page.
 * Called again when the reading mode changes.
 */
function watchEpubImages(): void {
  imageObserver?.disconnect();
  imageObserver = null;
  const bookmarkId = current?.id;
  if (bookmarkId == null || current?.article?.content_type !== 'epub') return;
  const images = [...$('reader-content').querySelectorAll<HTMLImageElement>('img[id^="epub-img-"]')]
    .filter((img) => !img.dataset.requested);
  if (!images.length) return;

  const paged = isTextPaged();
  imageObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const img = entry.target as HTMLImageElement;
      imageObserver?.unobserve(img);
      img.dataset.requested = '';

      const file = IMAGE_ID.exec(img.id)?.[1];
      if (!file) continue;
      if (!imageLoads.has(file)) imageLoads.set(file, fetchEpubImage(bookmarkId, file));
      void imageLoads.get(file)!.then((url) => {
        if (url && img.isConnected) img.src = url;
      });
    }
  }, paged ? { root: $('reader-article'), rootMargin: '0px 100%' } : { rootMargin: IMAGE_MARGIN });
  for (const img of images) imageObserver.observe(img);
}

function releaseEpubImages(): void {
  imageObserver?.disconnect();
  imageObserver = null;
  for (const load of imageLoads.values()) void load.then((url) => url && URL.revokeObjectURL(url));
  imageLoads.clear();
}

/** Where a `#fragment` link inside the article points. */
function findFragmentTarget(fragment: string): HTMLElement | null {
  let id = fragment;
  try {
    id = decodeURIComponent(fragment);
  } catch {
    // Not a valid escape; use it as written.
  }
  const root = $('reader-content');
  // EPUB chapters are marked with an attribute rather than an id (see epub.ts).
  const chapter = /^epub-chapter-(\d+)$/.exec(id);
  if (chapter) return root.querySelector<HTMLElement>(`[data-epub-chapter="${chapter[1]}"]`);
  return [...root.querySelectorAll<HTMLElement>('[id]')].find((element) => element.id === id) ?? null;
}

// A plain `<a href="#section">` would change the URL hash, which this app's
// router reads as "leave the reader" — every in-page link (a markdown file's
// contents, a footnote, an EPUB's cross-chapter link) sent you back to the
// list. Scroll to the target ourselves and leave the URL alone.
$('reader-content').addEventListener('click', (event) => {
  const link = (event.target as HTMLElement).closest<HTMLAnchorElement>('a[href^="#"]');
  if (!link) return;
  event.preventDefault();
  reveal(findFragmentTarget(link.getAttribute('href')!.slice(1)));
});

function renderError(message: string, bookmark: Bookmark): void {
  $('reader-content').replaceChildren(
    el('div', { class: 'reader-error' }, [
      el('p', {}, message),
      el('p', {}, [
        'You can still ',
        el('a', { href: bookmark.url, target: '_blank', rel: 'noopener noreferrer' }, 'read it at the source'),
        '.',
      ]),
      el('button', {
        class: 'btn-sm',
        type: 'button',
        onclick: async (e: Event) => {
          const button = e.target as HTMLButtonElement;
          button.disabled = true;
          button.textContent = 'fetching...';
          try {
            await api.archive(bookmark.id, {});
            await openReader(bookmark.id);
          } catch (err) {
            button.disabled = false;
            button.textContent = 'retry archiving';
            alert((err as Error).message);
          }
        },
      }, 'retry archiving'),
    ])
  );
}

// ── A YouTube video or a podcast episode ─────────────────────────────────────
// The player sits above the transcript, kept in view. Each paragraph starts
// with its timestamp, which seeks it; while it plays, the line being said is
// marked and kept in view unless you have scrolled away. The position saved is
// a point in the video or episode, whether you got there by watching,
// listening or reading: until it has been played, it is the line you are at.

interface Watching {
  /** Every caption line, in order, and its start in seconds. */
  lines: HTMLElement[];
  times: number[];
  now: number;
  tick: number | null;
  /** When the reader last scrolled by hand; following along waits a while after. */
  scrolledAt: number;
  listeners: AbortController;
  /** Set once the player has loaded (a YouTube player takes a moment). */
  player: MediaPlayer | null;
  /** The length the feed gave, until the player knows better. */
  length: number | null;
}

let watching: Watching | null = null;

const FOLLOW_PAUSE_MS = 5000;
const SAVE_EVERY_MS = 10_000;

/** Where the text in view begins: under the toolbar, and the player when there is one. */
function readingLine(): number {
  const toolbar = document.querySelector('.reader-toolbar')?.getBoundingClientRect().bottom ?? 0;
  const player = $('reader-media');
  return Math.max(toolbar, player.hidden ? 0 : player.getBoundingClientRect().bottom) + 8;
}

/** The last line starting at or before `seconds`. */
function lineAt(seconds: number): number {
  const { times } = watching!;
  let low = 0;
  let high = times.length - 1;
  let found = -1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (times[mid]! <= seconds) {
      found = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return Math.max(0, found);
}

/** The time of the first line reaching below the reading line. */
function transcriptTime(): number {
  const { lines, times } = watching!;
  if (!lines.length) return 0;
  const line = readingLine();
  let low = 0;
  let high = lines.length - 1;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (lines[mid]!.getBoundingClientRect().bottom > line) high = mid;
    else low = mid + 1;
  }
  return times[low]!;
}

/** How long it is: the player's word, or until it has one, the feed's, or its last line. */
function mediaLength(): number {
  return watching?.player?.duration() ?? watching?.length ?? watching?.times.at(-1) ?? 0;
}

function videoRatio(): number {
  const w = watching;
  const length = mediaLength();
  if (!w || !length) return 0;
  const seconds = w.player?.started() ? (w.player.time() ?? 0) : transcriptTime();
  return Math.min(1, Math.max(0, seconds / length));
}

/** Scroll so an element sits just under the player. */
function scrollUnderVideo(element: Element, behavior: ScrollBehavior = 'smooth'): void {
  const top = window.scrollY + element.getBoundingClientRect().top - readingLine();
  window.scrollTo({ top: Math.max(0, top), behavior });
}

function follow(): void {
  const w = watching;
  const seconds = w?.player?.time() ?? null;
  if (!w || seconds === null || !w.lines.length) return;
  const index = lineAt(seconds);
  if (index === w.now) return;
  w.lines[w.now]?.classList.remove('is-now');
  w.now = index;
  const line = w.lines[index]!;
  line.classList.add('is-now');

  if (Date.now() - w.scrolledAt < FOLLOW_PAUSE_MS) return;
  const rect = line.getBoundingClientRect();
  if (rect.top < readingLine() || rect.bottom > window.innerHeight - 40) {
    const top = window.scrollY + rect.top - readingLine() - (window.innerHeight - readingLine()) * 0.25;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }
}

function onPlayingChange(playing: boolean): void {
  const w = watching;
  if (!w) return;
  if (w.tick !== null) clearInterval(w.tick);
  w.tick = null;
  if (playing) {
    let saved = Date.now();
    w.tick = window.setInterval(() => {
      follow();
      if (Date.now() - saved > SAVE_EVERY_MS) {
        saved = Date.now();
        scrollHandler?.();
      }
    }, 250);
  } else {
    scrollHandler?.();
  }
}

/** Every paragraph gets its timestamp: a button whose time is drawn by CSS
 *  from an attribute, so it has no text for highlights or copying to take in
 *  as part of what was said. */
function addTimestamps(): void {
  for (const paragraph of $('reader-content').querySelectorAll<HTMLElement>('p[data-t]')) {
    const seconds = Number(paragraph.dataset.t);
    const time = formatTime(seconds);
    paragraph.prepend(el('button', {
      type: 'button', class: 'yt-time', 'data-t': String(seconds), 'data-label': time,
      title: `Play from ${time}`, 'aria-label': `Play from ${time}`,
    }));
  }
}

function renderTranscriptPending(bookmark: Bookmark): void {
  const error = bookmark.article?.extract_error;
  $('reader-content').replaceChildren(el('div', { class: 'reader-error' }, [
    el('p', {}, error
      ? `No transcript: ${error}.`
      : 'The transcript is on its way. YouTube turns this server away, so the browser extension fetches '
        + 'it instead, within a few minutes of your desktop browser next being open.'),
    el('button', {
      class: 'btn-sm',
      type: 'button',
      onclick: async (event: Event) => {
        const button = event.target as HTMLButtonElement;
        button.disabled = true;
        button.textContent = 'trying...';
        try {
          await api.archive(bookmark.id, {});
        } catch {
          // Refused again, or still no captions: reopening shows which.
        }
        if (current?.id === bookmark.id) await openReader(bookmark.id);
      },
    }, 'try again'),
  ]));
}

function setMediaMode(kind: 'video' | 'audio' | null): void {
  $('reader-media').hidden = !kind;
  $('reader-media').dataset.kind = kind ?? '';
  $('reader-tts').hidden = Boolean(kind); // it has a voice of its own
  $('reader-article').classList.toggle('is-media', Boolean(kind));
  $('reader-prefs').classList.toggle('is-media', Boolean(kind));
}

/** An episode's show notes: all there is without a transcript, folded away above one. */
function showNotes(media: PodcastEpisodeMedia, transcript: boolean): HTMLElement | null {
  if (!media.notes_html) return null;
  const notes = el('div', { class: 'show-notes-body' });
  // Sanitized by the server when the episode was saved.
  notes.innerHTML = media.notes_html;
  if (!transcript) return notes;
  return el('details', { class: 'show-notes' }, [el('summary', {}, 'Show notes'), notes]);
}

/** An audiobook's cover beside its title, from its signed link. */
function showAudiobookCover(src: string, bookmark: Bookmark): void {
  $('reader-header').classList.add('has-cover');
  $('reader-header').prepend(el('img', { class: 'reader-cover', src, alt: `Cover of ${bookmark.title ?? 'this book'}` }));
}

/** Where an <audio> or <img> can load an API path from. */
function apiHref(apiPath: string): string {
  return new URL(apiPath, api.baseUrl).href;
}

/** A video, an episode or an audiobook: its player above its transcript (an
 *  audiobook's "transcript" being its list of chapters). */
async function openMediaReader(bookmark: Bookmark): Promise<void> {
  const article = bookmark.article!;
  const episode = article.media?.kind === 'episode' ? article.media : null;
  const audiobook = article.media?.kind === 'audiobook' ? article.media : null;
  const videoId = article.content_type === 'youtube' ? youtubeVideoId(bookmark.url) : null;
  if (!videoId && !episode && !audiobook) {
    renderError(article.content_type === 'youtube' ? 'This is not a YouTube video link.' : 'This episode has no audio.', bookmark);
    return;
  }
  setMediaMode(videoId ? 'video' : 'audio');
  const w: Watching = {
    lines: [], times: [], now: -1, tick: null, scrolledAt: 0, listeners: new AbortController(),
    player: null, length: episode?.duration ?? audiobook?.duration ?? null,
  };
  watching = w;

  if (article.has_content) {
    try {
      const { content_html: html } = await api.getContent(bookmark.id);
      // Built by the server from the captions or transcript — see the note at the top of this file.
      $('reader-content').innerHTML = html ?? '';
    } catch (err) {
      renderError((err as Error).message, bookmark);
    }
    addTimestamps();
    const notes = episode && showNotes(episode, true);
    if (notes) $('reader-content').prepend(notes);
    w.lines = [...$('reader-content').querySelectorAll<HTMLElement>('span[data-t]')];
    w.times = w.lines.map((line) => Number(line.dataset.t));
    showToc('Chapters', (article.toc ?? []).map(({ title, href, depth }) => ({
      label: title,
      depth,
      go: () => {
        const heading = findFragmentTarget(href);
        if (!heading) return;
        scrollUnderVideo(heading);
        w.player?.seek(Number(heading.dataset.t));
      },
      target: () => findFragmentTarget(href),
    })));
    await loadAnnotations(bookmark.id);
  } else if (episode) {
    const notes = showNotes(episode, false);
    $('reader-content').replaceChildren(
      el('p', { class: 'reader-note-muted' }, 'This show doesn\'t publish a transcript for this episode.'),
      ...(notes ? [notes] : []),
    );
    await loadAnnotations(bookmark.id);
  } else {
    renderTranscriptPending(bookmark);
  }
  if (current !== bookmark || watching !== w) return;

  // Scrolling by hand (not our own following along) holds the following off.
  const manual = (): void => { w.scrolledAt = Date.now(); };
  const { signal } = w.listeners;
  for (const type of ['wheel', 'touchmove', 'keydown'] as const) {
    window.addEventListener(type, manual, { signal, passive: true });
  }

  const ratio = Math.max(bookmark.reading?.scroll_ratio || 0, cachedProgress(bookmark.id));
  const start = ratio * mediaLength();
  if (start && w.lines.length) {
    requestAnimationFrame(() => scrollUnderVideo(w.lines[lineAt(start)]!, 'auto'));
  }
  paintProgress(ratio);
  attachListeners(bookmark.id);

  const frame = $('reader-media-frame');
  if (episode) {
    w.player = openAudio(frame, episode.audio_url, start, { onPlayingChange });
    return;
  }
  if (audiobook) {
    let links: AudiobookLinks;
    try {
      links = await api.getAudiobookLinks(bookmark.id);
    } catch (err) {
      frame.replaceChildren(el('div', { class: 'reader-error' }, (err as Error).message));
      return;
    }
    if (watching !== w) return;
    if (links.cover_url) showAudiobookCover(apiHref(links.cover_url), bookmark);
    w.player = openAudio(frame, apiHref(links.audio_url), start, { onPlayingChange }, {
      renew: async () => apiHref((await api.getAudiobookLinks(bookmark.id)).audio_url),
    });
    return;
  }
  openVideo(frame, videoId!, start, { onPlayingChange }).then((player) => {
    if (watching === w) w.player = player;
    else player.close();
  }).catch((err) => {
    frame.replaceChildren(el('div', { class: 'reader-error' }, (err as Error).message));
  });
}

function closeMediaReader(): void {
  const w = watching;
  if (!w) return;
  watching = null;
  if (w.tick !== null) clearInterval(w.tick);
  w.listeners.abort();
  w.player?.close();
  $('reader-media-frame').replaceChildren();
  setMediaMode(null);
}

// ── A podcast show ────────────────────────────────────────────────────────────
// Its description, and its episodes newest first, each saved as a bookmark of
// its own when you choose it — or opened, if it was saved before.

function episodeRow(showId: number, episode: FeedEpisodeSummary): HTMLElement {
  const facts = [
    episode.published ? formatDate(episode.published) : null,
    episode.duration ? `${Math.round(episode.duration / 60)} min` : null,
    episode.has_transcript ? 'transcript' : null,
  ].filter(Boolean).join(' · ');

  const action = el('button', {
    type: 'button',
    class: 'btn-sm',
    onclick: async (event: Event) => {
      if (episode.saved_id) {
        window.location.hash = `#/read/${episode.saved_id}`;
        return;
      }
      const button = event.target as HTMLButtonElement;
      button.disabled = true;
      button.textContent = 'saving...';
      try {
        const saved = await api.saveEpisode(showId, episode.key);
        window.location.hash = `#/read/${saved.id}`;
      } catch (err) {
        button.disabled = false;
        button.textContent = 'save';
        alert((err as Error).message);
      }
    },
  }, episode.saved_id ? 'open' : 'save');

  return el('li', { class: 'feed-episode' }, [
    el('div', { class: 'feed-episode-text' }, [
      el('div', { class: 'feed-episode-title' }, episode.title || 'Untitled episode'),
      el('div', { class: 'feed-episode-facts' }, facts),
    ]),
    action,
  ]);
}

async function openFeedReader(bookmark: Bookmark): Promise<void> {
  const media = bookmark.article?.media?.kind === 'feed' ? bookmark.article.media as PodcastFeedMedia : null;
  const description = el('div', { class: 'feed-description' });
  // Sanitized by the server when the show was saved.
  if (media?.description_html) description.innerHTML = media.description_html;
  const list = el('ol', { class: 'feed-episodes' }, el('li', { class: 'msg' }, 'loading episodes...'));
  const refresh = el('button', {
    type: 'button',
    class: 'btn-sm',
    title: 'Fetch the show\'s feed again for new episodes',
    onclick: async (event: Event) => {
      const button = event.target as HTMLButtonElement;
      button.disabled = true;
      button.textContent = 'refreshing...';
      try {
        await api.archive(bookmark.id, {});
      } catch (err) {
        alert((err as Error).message);
      }
      if (current?.id === bookmark.id) await openReader(bookmark.id);
    },
  }, 'refresh');

  $('reader-content').replaceChildren(
    description,
    el('div', { class: 'feed-episodes-head' }, [el('h2', {}, 'Episodes'), refresh]),
    list,
  );
  try {
    const episodes = await api.getEpisodes(bookmark.id);
    if (current?.id !== bookmark.id) return;
    list.replaceChildren(...(episodes.length
      ? episodes.map((episode) => episodeRow(bookmark.id, episode))
      : [el('li', { class: 'msg' }, 'No episodes with audio in this feed.')]));
  } catch (err) {
    list.replaceChildren(el('li', { class: 'reader-error' }, (err as Error).message));
  }
}

// A timestamp, or a chapter's heading, plays from there.
$('reader-content').addEventListener('click', (event) => {
  if (!watching) return;
  const target = (event.target as HTMLElement).closest<HTMLElement>('.yt-time, h2[data-t]');
  if (target) watching.player?.seek(Number(target.dataset.t));
});

// The player stays in view below the toolbar, whose height changes as it wraps.
new ResizeObserver(([entry]) => {
  const height = (entry!.target as HTMLElement).offsetHeight;
  document.documentElement.style.setProperty('--reader-toolbar-height', `${height}px`);
}).observe(document.querySelector('.reader-toolbar')!);

// ── Lifecycle ─────────────────────────────────────────────────────────────────

export async function openReader(id: number): Promise<void> {
  applyPrefs(prefs);
  setReaderMode('html');
  $('reader-content').replaceChildren(el('div', { class: 'msg' }, 'loading...'));
  window.scrollTo(0, 0);

  let bookmark: Bookmark;
  try {
    bookmark = await api.getBookmark(id);
  } catch (err) {
    $('reader-content').replaceChildren(el('div', { class: 'reader-error' }, (err as Error).message));
    return;
  }

  current = bookmark;
  document.title = `${bookmark.title || 'Reading'} — Altfresh`;
  renderHeader(bookmark);
  if (bookmark.article?.content_type === 'epub') void loadEpubCover(id);
  renderFooter(bookmark);
  renderStatusButton(bookmark.reading?.status || 'unread');

  const type = bookmark.article?.content_type;
  if (type === 'youtube' || type === 'podcast' || type === 'audiobook') {
    await openMediaReader(bookmark);
    return;
  }
  if (type === 'podcast_feed') {
    // Read aloud, a show's page would be a list of episode titles.
    $('reader-tts').hidden = true;
    await openFeedReader(bookmark);
    restoreScroll(Math.max(bookmark.reading?.scroll_ratio || 0, cachedProgress(id)));
    attachListeners(id);
    return;
  }

  if (!bookmark.article?.has_content) {
    renderError(
      bookmark.article?.extract_error || 'This bookmark has no archived article yet.',
      bookmark
    );
    return;
  }

  if (bookmark.article?.content_type === 'pdf') {
    setReaderMode('pdf');
    preloadPdfViewer();
    try {
      // Highlights load first so each page can paint its own the moment its
      // text layer exists, rather than being repainted afterwards.
      await loadAnnotations(id);
      const setAspect = (aspect: number): void => {
        $('reader-article').style.setProperty('--pdf-aspect', String(aspect));
      };
      const info = await openPdf($('pdf-content'), id, { onTextLayer: paintPdfPage, onPageAspect: setAspect });
      setAspect(info.pageAspect);
      setupPdfControls(info.pageCount, info.loadOutline);
    } catch (err) {
      // renderError writes into #reader-content, so switch back to the HTML
      // view for it to actually be visible.
      setReaderMode('html');
      renderError((err as Error).message, bookmark);
      return;
    }
  } else {
    try {
      const { content_html: html } = await api.getContent(id);
      // Sanitized server-side on ingest — see the note at the top of this file.
      $('reader-content').innerHTML = html ?? '';
    } catch (err) {
      renderError((err as Error).message, bookmark);
      return;
    }

    if (bookmark.article?.content_type === 'epub') {
      updateEpubChapters(bookmark.article.toc);
      releaseEpubImages();
      reserveEpubImages();
    }
    else {
      hideDuplicateTitle(bookmark.title);
      updateHeadingContents();
    }

    // Annotations are painted into the article, so they must land before the
    // scroll is restored — highlights change the rendered height.
    await loadAnnotations(id);

    // Sentence markers for jumping around while listening — best effort. This is
    // plain text splitting server-side, not TTS generation, so it works even
    // when OpenAI is unreachable; failure here shouldn't block reading.
    try {
      const { sentences } = await api.getAudioSentences(id);
      markSentences(sentences);
    } catch (err) {
      console.error('Failed to load sentence markers', err);
    }

    // Paged, the columns are laid out from the finished article: highlights
    // and sentence marks are in, and every picture has its space reserved.
    if (current !== bookmark) return;
    if (getTextReading() === 'paged') enableTextPaging($('reader-article'), $('reader-content'), { keepPlace: false });
    syncPageButtons();
    watchEpubImages();
  }

  // Prefer whichever position is further along: the server knows about other
  // devices, the local cache knows about progress made while offline.
  const ratio = Math.max(bookmark.reading?.scroll_ratio || 0, cachedProgress(id));
  restoreScroll(ratio);

  attachListeners(id);
}

function attachListeners(id: number): void {
  detachListeners();

  scrollHandler = () => {
    const ratio = currentRatio();
    paintProgress(ratio);
    cacheProgress(id, ratio);
    persistProgress(id, ratio);
  };
  window.addEventListener('scroll', scrollHandler, { passive: true });

  keyHandler = (event) => {
    const target = event.target as HTMLElement;
    if (target.matches('input, textarea, select') || target.isContentEditable) return;

    if (isPdfPaged() || isTextPaged()) {
      const keys = { ArrowRight: 1, PageDown: 1, j: 1, ArrowLeft: -1, PageUp: -1, k: -1 } as const;
      const turn = event.key === ' ' ? (event.shiftKey ? -1 : 1) : keys[event.key as keyof typeof keys];
      if (turn) {
        event.preventDefault();
        if (isPdfPaged()) turnPdfPage(turn);
        else turnTextPage(turn);
        return;
      }
    }

    if (event.key === 'j') window.scrollBy({ top: window.innerHeight * 0.4, behavior: 'smooth' });
    else if (event.key === 'k') window.scrollBy({ top: -window.innerHeight * 0.4, behavior: 'smooth' });
    else if (event.key === 'h') highlightSelection();
    else if (event.key === 'Escape') {
      const panel = $('reader-prefs');
      if (!panel.classList.contains('hidden')) {
        panel.classList.add('hidden');
        $('reader-prefs-btn').setAttribute('aria-expanded', 'false');
      } else {
        window.location.hash = '';
      }
    }
  };
  window.addEventListener('keydown', keyHandler);
}

function detachListeners(): void {
  if (scrollHandler) window.removeEventListener('scroll', scrollHandler);
  if (keyHandler) window.removeEventListener('keydown', keyHandler);
  scrollHandler = null;
  keyHandler = null;
}

export function closeReader(): void {
  if (!current) return;
  // Flush the pending debounced write rather than dropping the last position.
  persistProgress.flush(current.id, currentRatio());
  stopAudio();
  detachListeners();
  releaseEpubImages();
  releaseCover();
  unloadAnnotations();
  unloadPdf();
  disableTextPaging({ keepPlace: false });
  closeMediaReader();
  closeNotes();
  current = null;
  document.title = 'Altfresh — Bookmarks & Reading';
  $('reader-content').replaceChildren();
  $('pdf-content').replaceChildren();
  sentenceMarks = [];
  currentSentenceIndex = null;
}

function toggleNotes(): void {
  $('annotation-panel').classList.toggle('collapsed');
}

$('reader-notes').addEventListener('click', toggleNotes);
$('annotation-toggle').addEventListener('click', toggleNotes);

// Keep the toolbar's Notes button showing how many there are.
setCountListener((count: number) => {
  $('reader-notes').textContent = count ? `Notes ${count}` : 'Notes';
});

// Fetched rather than linked: the endpoint needs an Authorization header,
// which a plain <a href> can't send.
$('reader-export').addEventListener('click', async () => {
  if (!current) return;
  const button = $<HTMLButtonElement>('reader-export');
  button.disabled = true;
  try {
    const markdown = await api.request<string>(`/bookmarks/${current!.id}/export.md`, { raw: true });
    const url = URL.createObjectURL(new Blob([markdown], { type: 'text/markdown;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(current!.title || 'article').replace(/[^\p{L}\p{N}]+/gu, '-').slice(0, 60)}.md`;
    link.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    alert((err as Error).message);
  } finally {
    button.disabled = false;
  }
});

// Same manual-fetch-with-Bearer-header pattern as export above — the
// endpoint needs auth a plain <a href> can't send, and this one is binary.
// Shared by the PDF and EPUB download buttons, which differ only in which
// endpoint/extension they hit.
async function downloadArticleFile(kind: 'pdf' | 'epub', button: HTMLButtonElement): Promise<void> {
  if (!current) return;
  button.disabled = true;
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const response = await fetch(`${api.baseUrl || ''}/bookmarks/${current.id}/${kind}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error(`Could not download the ${kind.toUpperCase()} (HTTP ${response.status})`);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(current.title || 'document').replace(/[^\p{L}\p{N}]+/gu, '-').slice(0, 60)}.${kind}`;
    link.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    alert((err as Error).message);
  } finally {
    button.disabled = false;
  }
}

$('reader-download-pdf').addEventListener('click', () => {
  downloadArticleFile('pdf', $<HTMLButtonElement>('reader-download-pdf'));
});
$('reader-download-epub').addEventListener('click', () => {
  downloadArticleFile('epub', $<HTMLButtonElement>('reader-download-epub'));
});

$('reader-toc-btn').addEventListener('click', () => {
  const panel = $('reader-toc');
  const open = panel.classList.toggle('hidden');
  $('reader-toc-btn').setAttribute('aria-expanded', String(!open));
  if (!open) markCurrentNavEntry();
});

$('page-input').addEventListener('change', (event) => {
  const input = event.target as HTMLInputElement;
  const page = Number(input.value);
  if (Number.isFinite(page)) {
    if (mode === 'pdf') scrollToPdfPage(page);
    else goToTextPage(page);
  }
  input.blur();
});
function turnPage(direction: 1 | -1): void {
  if (mode === 'pdf') turnPdfPage(direction);
  else turnTextPage(direction);
}
$('page-prev').addEventListener('click', () => turnPage(-1));
$('page-next').addEventListener('click', () => turnPage(1));
$('pdf-zoom-in').addEventListener('click', () => { stepPdfZoom(1); updateZoomLabel(); });
$('pdf-zoom-out').addEventListener('click', () => { stepPdfZoom(-1); updateZoomLabel(); });
$('pdf-zoom-level').addEventListener('click', () => { resetPdfZoom(); updateZoomLabel(); });

interface AudioChunk {
  url: string;
  index: number;
}

type PlayerState = 'idle' | 'playing' | 'paused';

let streamController: AbortController | null = null;
let currentAudio: HTMLAudioElement | null = null;
let audioQueue: AudioChunk[] = [];
let playerState: PlayerState = 'idle';
/** Whether a chunk is actively assigned to `currentAudio` right now — distinct
 *  from `playerState`, which also covers "playing but between chunks". */
let chunkPlaying = false;
/** Wrapper elements per sentence index — may be empty for a sentence whose
 *  text couldn't be located in the rendered article. */
let sentenceMarks: HTMLElement[][] = [];
let currentSentenceIndex: number | null = null;

const SPEED_KEY = 'nexus_tts_speed';
let playbackRate = Number(localStorage.getItem(SPEED_KEY)) || 1;

/**
 * Wrap every sentence of the article in a clickable `[data-sentence-index]`
 * marker, reusing the same flatten/locate machinery annotations use to
 * anchor a quote — the sentence list came from markdown, the article text
 * from rendered HTML, so an exact match isn't guaranteed and `locate`'s
 * fuzzy fallback absorbs that. This only locates text; no audio is
 * requested, so it's safe to do as soon as the article renders.
 */
function markSentences(sentences: string[]): void {
  clearSentenceMarks();
  if (!sentences.length) return;

  const root = $('reader-content');
  const { text, positions } = flatten(root);

  // Phase 1: locate every sentence against the one flatten() snapshot, in
  // article order, using each match's end as the next search's hint — a
  // repeated phrase then resolves to its next real occurrence instead of
  // jumping back to the first one every time. Nothing is wrapped yet.
  let hint = 0;
  const located: ({ start: number; end: number } | null)[] = [];
  for (const sentence of sentences) {
    const match = locate(text, { quote: normalizeText(sentence), position_hint: hint });
    if (match.method === 'orphan') {
      located.push(null);
      continue;
    }
    hint = match.end;
    located.push({ start: match.start, end: match.end });
  }

  // Phase 2: wrap, highest offset first (wrapRanges explains why that has to
  // be by offset rather than by sentence number).
  sentenceMarks = wrapRanges(positions, located, (index) => {
    const span = document.createElement('span');
    span.className = 'tts-sentence';
    span.dataset.sentenceIndex = String(index);
    return span;
  });
}

function clearSentenceMarks(): void {
  const root = $('reader-content');
  for (const wrap of [...root.querySelectorAll('.tts-sentence')]) {
    const parent = wrap.parentNode;
    if (!parent) continue;
    while (wrap.firstChild) parent.insertBefore(wrap.firstChild, wrap);
    parent.removeChild(wrap);
  }
  root.normalize();
  sentenceMarks = [];
  currentSentenceIndex = null;
}

/** Move the "currently reading" mark, without re-searching the DOM — every
 *  sentence is already wrapped, so this is just a class toggle. */
function setCurrentSentence(index: number | null): void {
  if (currentSentenceIndex !== null) {
    for (const el of sentenceMarks[currentSentenceIndex] ?? []) el.classList.remove('is-current');
  }
  currentSentenceIndex = index;
  if (index !== null) {
    const els = sentenceMarks[index] ?? [];
    for (const el of els) el.classList.add('is-current');
    if (isTextPaged()) {
      if (els[0]) revealText(els[0]);
    } else {
      els[0]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }
}

function updatePlayerUI(): void {
  // Sentence hover/click only means something while listening (playing or
  // paused) — otherwise it's an invisible, easy-to-trigger way to start audio
  // while just reading or selecting text. Every state change funnels through
  // here, so this one line keeps the class in step with playerState.
  $('reader-content').classList.toggle('is-listening', playerState !== 'idle');

  const listenBtn = $('reader-listen');
  const speedSelect = $('reader-tts-speed');
  const stopBtn = $('reader-tts-stop');

  if (playerState === 'idle') {
    listenBtn.textContent = '▶ Listen';
    listenBtn.title = 'Listen to article';
    speedSelect.hidden = true;
    stopBtn.hidden = true;
  } else {
    listenBtn.textContent = playerState === 'playing' ? '⏸ Pause' : '▶ Resume';
    listenBtn.title = playerState === 'playing' ? 'Pause' : 'Resume';
    speedSelect.hidden = false;
    stopBtn.hidden = false;
  }
}

function stopAudio(): void {
  if (streamController) {
    streamController.abort();
    streamController = null;
  }
  if (currentAudio) {
    currentAudio.pause();
    currentAudio = null;
  }
  audioQueue = [];
  chunkPlaying = false;
  playerState = 'idle';
  setCurrentSentence(null);
  updatePlayerUI();
}

function playNextChunk(): void {
  if (playerState !== 'playing' || audioQueue.length === 0) {
    chunkPlaying = false;
    return;
  }
  chunkPlaying = true;
  const next = audioQueue.shift()!;
  setCurrentSentence(next.index);
  currentAudio = new Audio(next.url);
  currentAudio.playbackRate = playbackRate;
  currentAudio.play().catch(e => console.error("Playback failed", e));
  currentAudio.onended = () => {
    playNextChunk();
  };
}

/** Start (or restart) playback from a given sentence index — used both by
 *  the Listen button (index 0) and by clicking any sentence in the article. */
function startListening(fromIndex: number): void {
  if (!current) return;

  if (streamController) streamController.abort();
  currentAudio?.pause();
  currentAudio = null;
  audioQueue = [];
  chunkPlaying = false;
  playerState = 'playing';
  updatePlayerUI();

  const token = localStorage.getItem(TOKEN_KEY);
  const url = `${api.baseUrl || ''}/bookmarks/${current.id}/audio/stream?from=${fromIndex}`;
  streamController = new AbortController();
  const { signal } = streamController;

  fetch(url, {
    headers: { 'Authorization': `Bearer ${token}` },
    signal,
  }).then(async response => {
    if (!response.ok || !response.body) {
      throw new Error(`Audio stream request failed: ${response.status}`);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.done) {
              console.log("Audio generation complete!");
            } else if (data.chunkUrl) {
              audioQueue.push({
                url: `${api.baseUrl || ''}${data.chunkUrl}`,
                index: data.index,
              });
              if (!chunkPlaying) playNextChunk();
            }
          } catch (e) {
            // parse error
          }
        }
      }
    }
  }).catch(err => {
    if (signal.aborted) return;
    console.error('Stream error', err);
    stopAudio();
  });
}

function togglePlayPause(): void {
  if (!current) return;

  if (playerState === 'playing') {
    currentAudio?.pause();
    playerState = 'paused';
    updatePlayerUI();
  } else if (playerState === 'paused') {
    playerState = 'playing';
    updatePlayerUI();
    if (currentAudio) {
      currentAudio.play().catch(e => console.error('Playback failed', e));
    } else {
      // Paused between chunks (or before the first one arrived) — resume
      // the normal advance-from-queue logic instead of resuming an element.
      playNextChunk();
    }
  } else {
    startListening(0);
  }
}

$('reader-listen').addEventListener('click', togglePlayPause);
$('reader-tts-stop').addEventListener('click', stopAudio);

$('reader-content').addEventListener('click', (event) => {
  // Jump-to-sentence is a listening-mode interaction (see updatePlayerUI).
  if (!current || playerState === 'idle') return;
  const target = (event.target as HTMLElement).closest<HTMLElement>('[data-sentence-index]');
  if (!target) return;
  const index = Number(target.dataset.sentenceIndex);
  if (Number.isInteger(index)) startListening(index);
});

const speedSelect = $<HTMLSelectElement>('reader-tts-speed');
speedSelect.value = String(playbackRate);
speedSelect.addEventListener('change', () => {
  playbackRate = Number(speedSelect.value) || 1;
  localStorage.setItem(SPEED_KEY, String(playbackRate));
  if (currentAudio) currentAudio.playbackRate = playbackRate;
});

$('reader-back').addEventListener('click', () => { window.location.hash = ''; });
$('reader-status').addEventListener('click', toggleStatus);
wirePrefs();

